import { useEffect, useEffectEvent, useState } from 'react';
import { useParams } from 'react-router';
import { Button, Card, Modal, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client/react';
import dayjs from 'dayjs';

import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import useCustomerInfo from 'hooks/useCustomerInfo';
import useModalControls from 'hooks/useModalControls';
import { GET_ACCOUNT_CARD_LAST_4 } from 'queries/account';
import { ONE_TIME_PAYMENT } from 'queries/payments';
import {
  getFundingAccountName,
  formatDollarsFromCents,
} from 'utilities/helpers';
import {
  SETTLEMENT_OFFER_TYPES,
  SETTLEMENT_OFFER_STATES,
} from 'utilities/constants';

import ChangeFundingAccount from './ChangeFundingAccount';
import {
  useRecoveriesState,
  setFundingAccount,
  setEftaDetails,
} from './recoveriesState';

const { Text, Paragraph } = Typography;

const ActiveOfferCard = ({ thirdParty }) => {
  const { customerId, accountId } = useParams();
  const { customerInfo } = useCustomerInfo();
  const { fundingAccount, offer, paymentPlan } = useRecoveriesState();

  const [settlementPercentage, setSettlementPercentage] = useState('');
  const fundingAccountModal = useModalControls();

  const isPendingCloseOutOrFulfilled =
    offer?.state === SETTLEMENT_OFFER_STATES.PENDING_CLOSE_OUT ||
    offer?.state === SETTLEMENT_OFFER_STATES.FULFILLED;

  const isPifOffer = offer?.offerType === SETTLEMENT_OFFER_TYPES.PIF;

  useEffect(() => {
    if (offer?.balance) {
      const settlementBalancePercentage =
        (offer.balance.settlementBalance / offer.balance.outstandingBalance) *
        100;

      setSettlementPercentage(`${Math.round(settlementBalancePercentage)}%`);
    }
  }, [offer?.balance]);

  const title = isPifOffer ? (
    <>
      Paid in Full |{' '}
      <Text type="secondary">
        100%
        {offer?.duration?.unit === 'MONTHS'
          ? `, ${offer?.duration.value} ${offer?.duration.unit.toLowerCase()}`
          : ' of outstanding balance'}
      </Text>
    </>
  ) : (
    <>
      Settlement |{' '}
      <Text type="secondary">
        {settlementPercentage}
        {offer?.duration?.unit === 'MONTHS'
          ? `, ${
              offer?.duration?.value
            } ${offer?.duration?.unit?.toLowerCase()}`
          : ' of outstanding balance'}
      </Text>
    </>
  );

  //FOR REVIEW: error handling
  const [getFundingAccounts, fundingAccountsQuery] = useLazyQuery(
    ONE_TIME_PAYMENT,
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    },
  );

  const [getCardLast4, cardLast4Query] = useLazyQuery(
    GET_ACCOUNT_CARD_LAST_4,
    {},
  );
  const cardLast4 = cardLast4Query.data?.account?.cardDetails?.last4 || '';

  useEffect(() => {
    getCardLast4({ variables: { customerId, accountId } });

    const customerName = customerInfo.fullName;
    const email = customerInfo.email;

    const fundingAccountLast4 = fundingAccount?.name?.substring(
      fundingAccount.name.length - 4,
      fundingAccount.name.length,
    );

    if (cardLast4 && fundingAccountLast4) {
      setEftaDetails({ customerName, email, cardLast4, fundingAccountLast4 });
    }
  }, [customerId, fundingAccount, cardLast4]);

  // cant use a memo here since we're setting state externally
  const handleFundingAccountData = useEffectEvent((data) => {
    const fundingAccounts = data?.fundingAccounts || [];
    const fundingAccountName = getFundingAccountName(
      fundingAccounts,
      fundingAccount.id,
    );
    setFundingAccount(fundingAccount.id, fundingAccountName);
  });

  //using useEffect so notifyOnNetworkStatusChange requests store state
  useEffect(() => {
    if (fundingAccountsQuery.data) {
      handleFundingAccountData(fundingAccountsQuery.data);
    }
  }, [fundingAccountsQuery.data]);

  useEffect(() => {
    getFundingAccounts({
      variables: { customerId, accountId },
    });
  }, [customerId, accountId]);

  return (
    <>
      <Card type="inner" title={title}>
        <Paragraph>
          Program Amount:{' '}
          <Text strong>
            {formatDollarsFromCents(offer.balance.settlementBalance)}
          </Text>
          <br />
          Remaining Balance:{' '}
          {isPendingCloseOutOrFulfilled ? (
            <Text strong mark>
              {formatDollarsFromCents(offer.balance.remainingBalance)}
            </Text>
          ) : (
            <Text strong>
              {formatDollarsFromCents(offer.balance.remainingBalance)}
            </Text>
          )}
        </Paragraph>
        <Paragraph>
          <Text strong>
            Plan Due Date: {dayjs(offer.expiration).format('LL')}
          </Text>
        </Paragraph>
        {fundingAccount.id && (
          <>
            <Text strong>Funding Account</Text>
            {!isPendingCloseOutOrFulfilled && (
              <Button
                icon={<EditOutlined />}
                onClick={fundingAccountModal.show}
                type="text"
              />
            )}
            <br />
            <QueryBoundary
              query={fundingAccountsQuery}
              skeleton={{
                title: false,
                paragraph: { rows: 1 },
              }}
            >
              <Text type="secondary">{fundingAccount?.name ?? ''}</Text>
            </QueryBoundary>
          </>
        )}
        {!fundingAccount.id && offer?.isDebitCard && (
          <Paragraph>
            <Text strong> Payment Method: Debit Card</Text>
          </Paragraph>
        )}
        {!fundingAccount.id && !offer?.isDebitCard && (
          <>
            <Text strong>Funding Account</Text>
            <Button
              icon={<EditOutlined />}
              onClick={fundingAccountModal.show}
              type="text"
            />
            <br />
            <Text type="secondary">No Funding Account</Text>
          </>
        )}
      </Card>
      <Modal {...fundingAccountModal} footer={[]}>
        <ChangeFundingAccount
          onSuccess={fundingAccountModal.hide}
          thirdParty={thirdParty}
        />
      </Modal>
    </>
  );
};

export default ActiveOfferCard;
